import { Box } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { FolderInputRow } from '../../molecules/folder-label/FolderInputRow';
import { FolderItemRow } from '../../molecules/folder-label/FolderItemRow';
import type { FolderData } from '../../../types/folder-label';

interface FolderTreeItemProps {
    folder: FolderData;
    level?: number;
    onDelete: (id: string) => void;
    onAddSubfolder: (parentId: string, name: string) => void;
}

const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
    folder,
    level = 0,
    onDelete,
    onAddSubfolder,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showAddSubfolder, setShowAddSubfolder] = useState(false);
    const [subfolderName, setSubfolderName] = useState('');

    const hasChildren = useMemo(
        () => folder.children && folder.children.length > 0,
        [folder.children]
    );

    const handleAddSubfolder = useCallback(() => {
        if (subfolderName.trim()) {
            onAddSubfolder(folder.id, subfolderName.trim());
            setSubfolderName('');
            setShowAddSubfolder(false);
        }
    }, [subfolderName, onAddSubfolder, folder.id]);

    const handleCancelAdd = useCallback(() => {
        setShowAddSubfolder(false);
        setSubfolderName('');
    }, []);

    const handleToggleExpand = useCallback(() => setIsExpanded((prev) => !prev), []);

    const handleShowAddSubfolder = useCallback(() => {
        setShowAddSubfolder(true);
        setIsExpanded(true);
    }, []);

    const handleDelete = useCallback(() => onDelete(folder.id), [onDelete, folder.id]);

    return (
        <Box>
            <FolderItemRow
                name={folder.name}
                hasChildren={hasChildren || showAddSubfolder}
                isExpanded={isExpanded}
                indentLevel={level}
                onToggleExpand={handleToggleExpand}
                onAddSubfolder={handleShowAddSubfolder}
                onDelete={handleDelete}
            />

            {isExpanded && (
                <Box>
                    {showAddSubfolder && (
                        <FolderInputRow
                            value={subfolderName}
                            onChange={setSubfolderName}
                            onSubmit={handleAddSubfolder}
                            onCancel={handleCancelAdd}
                            placeholder="Enter subfolder name..."
                            indentLevel={level + 1}
                        />
                    )}
                    {hasChildren &&
                        folder.children.map((child) => (
                            <FolderTreeItem
                                key={child.id}
                                folder={child}
                                level={level + 1}
                                onDelete={onDelete}
                                onAddSubfolder={onAddSubfolder}
                            />
                        ))}
                </Box>
            )}
        </Box>
    );
};

interface FolderTreeProps {
    categoryFolder?: FolderData;
    onDelete: (id: string) => void;
    onAddSubfolder: (parentId: string, name: string) => void;
    rootFolderName: string;
    rootFolderHint: string;
}

export const FolderTree: React.FC<FolderTreeProps> = ({
    categoryFolder,
    onDelete,
    onAddSubfolder,
    rootFolderName,
    rootFolderHint,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showAddSubfolder, setShowAddSubfolder] = useState(false);
    const [subfolderName, setSubfolderName] = useState('');

    const hasChildren = useMemo(
        () => (categoryFolder?.children && categoryFolder.children.length > 0) || showAddSubfolder,
        [categoryFolder?.children, showAddSubfolder]
    );

    const handleToggleExpand = useCallback(() => setIsExpanded((prev) => !prev), []);

    const handleShowAddSubfolder = useCallback(() => {
        setShowAddSubfolder(true);
        setIsExpanded(true);
    }, []);

    const handleAddSubfolder = useCallback(() => {
        if (subfolderName.trim() && categoryFolder) {
            onAddSubfolder(categoryFolder.id, subfolderName.trim());
            setSubfolderName('');
            setShowAddSubfolder(false);
        }
    }, [subfolderName, onAddSubfolder, categoryFolder]);

    const handleCancelAdd = useCallback(() => {
        setShowAddSubfolder(false);
        setSubfolderName('');
    }, []);

    return (
        <Box>
            <FolderItemRow
                name={rootFolderName}
                hint={rootFolderHint}
                hasChildren={hasChildren}
                isExpanded={isExpanded}
                indentLevel={0}
                onToggleExpand={handleToggleExpand}
                onAddSubfolder={handleShowAddSubfolder}
                isDeletable={false}
            />

            {isExpanded && (
                <Box>
                    {showAddSubfolder && (
                        <FolderInputRow
                            value={subfolderName}
                            onChange={setSubfolderName}
                            onSubmit={handleAddSubfolder}
                            onCancel={handleCancelAdd}
                            placeholder="Enter subfolder name..."
                            indentLevel={1}
                        />
                    )}
                    {categoryFolder?.children.map((child) => (
                        <FolderTreeItem
                            key={child.id}
                            folder={child}
                            level={1}
                            onDelete={onDelete}
                            onAddSubfolder={onAddSubfolder}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
